import { useState } from "react";

const CATEGORIES = [
  {
    id: "women",
    label: "Women's Hair",
    intro: "Cuts, styles, and signature finishes.",
    items: [
      { name: "Haircut & Style", price: "$90+" },
      { name: "Haircut Only (no blow dry)", price: "$65+" },
      { name: "Blow Dry & Styling", price: "$55+" },
      { name: "Updos & Fancy Hair", price: "$85+" },
      { name: "Weddings, Proms & Parties", price: "On Consult" },
    ],
  },
  {
    id: "colour",
    label: "Schwarzkopf Colour",
    intro: "Free consultations — every head is one of a kind.",
    items: [
      { name: "Roots — Permanent / Semi / Organic", price: "$90+" },
      { name: "Specialties — Balayage, Ombré, Babylights", price: "$290+" },
      { name: "Full Head Highlights", price: "$180+" },
      { name: "Half Head Highlights", price: "$100+" },
      { name: "Lowlights", price: "$90+" },
      { name: "All-Over Pre-Lightening", price: "$60+" },
      { name: "Toner Application", price: "$60+" },
      { name: "Colour Refresher", price: "$60+" },
      { name: "Hair Gloss", price: "$50+" },
      { name: "Colour Correction", price: "On Consult" },
    ],
  },
  {
    id: "men",
    label: "Men & Kids",
    intro: "Precision cuts for every generation.",
    items: [
      { name: "Men's Colour with Cut", price: "$70+" },
      { name: "Men's Haircut", price: "$40+" },
      { name: "Men's Perms (incl. haircut)", price: "$150+" },
      { name: "Men's Hi-Lites (incl. haircut)", price: "$120+" },
      { name: "Girls, ages 1–10", price: "$35+" },
      { name: "Tween Girls, 11–15", price: "$55+" },
      { name: "Boys, ages 1–10", price: "$25+" },
      { name: "Tween Boys, 11–15", price: "$35+" },
    ],
  },
  {
    id: "treatments",
    label: "Treatments & Texture",
    intro: "Reclaim softness, shine and movement.",
    items: [
      { name: "Hair Botox Treatment", price: "$250+" },
      { name: "Keratin Smoothing Treatment", price: "$300+" },
      { name: "Body Perms & Waves", price: "$180+" },
      { name: "K18 Peptide Mask", price: "$50" },
      { name: "Fiber Plex Bond Protection", price: "$25" },
      { name: "911 / Keratin Mask", price: "$20" },
      { name: "In-Salon Hair Gloss", price: "$20" },
    ],
  },
  {
    id: "extensions",
    label: "Extensions (Remy)",
    intro: "Free consultation & colour match.",
    items: [
      { name: "Tape-In Extensions", price: "On Consult" },
      { name: "Fusion Extensions", price: "On Consult" },
      { name: "Clip-In Extensions", price: "On Consult" },
      { name: "Halo Extensions", price: "On Consult" },
    ],
  },
];

export default function Services() {
  const [active, setActive] = useState("women");
  const current = CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-36 bg-sand-50"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 lg:mb-20">
          <div className="lg:col-span-7">
            <span className="overline">The Menu</span>
            <h2 className="mt-6 font-serif font-light text-5xl lg:text-7xl text-ink-900 leading-[1.02] tracking-tight">
              Services &<br />
              <span className="italic text-gold">Price List</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-16">
            <p className="text-lg text-ink-500 leading-relaxed">
              All pricing is a starting point. The finest work begins with a
              free consultation — we&apos;ll build a plan around your hair,
              your lifestyle, and your budget.
            </p>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-ink-500">
              24hr cancellation notice kindly appreciated
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-y border-black/10 flex flex-wrap gap-x-2 gap-y-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-testid={`service-tab-${cat.id}`}
              onClick={() => setActive(cat.id)}
              className={`px-5 lg:px-8 py-5 text-xs tracking-[0.2em] uppercase transition-all duration-300 border-b-2 -mb-px ${
                active === cat.id
                  ? "border-ink-900 text-ink-900"
                  : "border-transparent text-ink-500 hover:text-ink-900"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Active category */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <h3 className="font-serif text-4xl lg:text-5xl text-ink-900 leading-tight">
              {current.label}
            </h3>
            <p className="mt-4 text-base text-ink-500 leading-relaxed">
              {current.intro}
            </p>
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 border border-gold/40 text-gold text-[0.7rem] tracking-[0.2em] uppercase">
              Free Consultation
            </div>
          </div>

          <div className="lg:col-span-8">
            <ul className="flex flex-col">
              {current.items.map((item, i) => (
                <li
                  key={item.name}
                  data-testid={`service-item-${current.id}-${i}`}
                  className="service-row group"
                  style={{ animation: `fade-up 0.6s ease-out ${i * 0.05}s both` }}
                >
                  <span className="font-serif text-xl lg:text-2xl text-ink-900 group-hover:text-gold transition-colors duration-300">
                    {item.name}
                  </span>
                  <span className="leader" />
                  <span className="font-serif text-xl lg:text-2xl text-ink-900 font-medium">
                    {item.price}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
              <p className="text-sm text-ink-500 max-w-md">
                Custom packages available — colour + cut + treatment bundles priced after consultation.
              </p>
              <a
                href="tel:+19058222690"
                data-testid="services-call-btn"
                className="btn-primary"
              >
                Book Over the Phone
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
